import { AUDIO_FETCH_BUDGET_MS, fetchCachedAudio } from "../audio/audio-cache.js";
import { playPreparedBuffer } from "../audio/buffer-playback.js";
import type {
  Narrator,
  NarratorCapabilities,
  PlayOptions,
  PlaybackHandle,
  PrepareRequest,
  PreparedNarration,
} from "../audio/narrator.js";
import type { CveEntry } from "../cve/normalize.js";
import { requestEntry, requestSlot, type BroadcastSlot } from "./selector.js";

/**
 * A narrator backed by the Worker's broadcast.
 *
 * `prepare` asks the Worker which entry the slot carries. If the clip is in R2
 * it is downloaded and decoded; if not, the Worker is already producing it in
 * the background, so the narrator polls briefly instead of falling back to a
 * local model. There is no model in this path at all: a slot either arrives as
 * audio or it does not arrive.
 */

/** How long to wait for the Worker to finish the clip before giving up. */
export const BROADCAST_WAIT_MS = 15_000;
export const BROADCAST_POLL_MS = 1_500;

export interface BroadcastNarratorOptions {
  context: BaseAudioContext;
  voiceBus: AudioNode;
  origin: string;
  fetchImpl?: typeof fetch;
  waitMs?: number;
  pollMs?: number;
}

export interface ResolvedSlot {
  slot: BroadcastSlot;
  entry: CveEntry;
  prepared: PreparedNarration;
}

/** Thrown when a slot produced no narration, so the caller can try a fallback. */
export class BroadcastMissError extends Error {
  constructor(slotIndex: number, reason: string) {
    super(`no narration for slot ${slotIndex}: ${reason}`);
    this.name = "BroadcastMissError";
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export class BroadcastNarrator implements Narrator {
  readonly id = "cached" as const;
  readonly capabilities: NarratorCapabilities = {
    graphRoutable: true,
    volumeControl: true,
    seekable: true,
    duckable: true,
  };

  constructor(private readonly options: BroadcastNarratorOptions) {}

  async init(): Promise<void> {
    // Nothing local to load. The model is the thing this path exists to avoid.
  }

  /**
   * Ask about a slot without waiting for production. The pre-gate call uses
   * this: it tells the Worker what the listener is about to play, so a clip is
   * ready by the time they press the gate.
   */
  async prime(slotIndex: number | null, signal: AbortSignal): Promise<BroadcastSlot | null> {
    return requestSlot(this.options.origin, slotIndex, {
      fetchImpl: this.options.fetchImpl,
      signal,
    });
  }

  /**
   * One named disclosure, for the prelude a `/CVE-...` link asks for. It is not
   * the minute's entry, so none of the slot checks apply; what matters is that
   * the clip arrives and belongs to the id that was asked for.
   */
  async resolveEntry(id: string, signal: AbortSignal): Promise<ResolvedSlot | null> {
    const waitMs = this.options.waitMs ?? BROADCAST_WAIT_MS;
    const pollMs = this.options.pollMs ?? BROADCAST_POLL_MS;
    const deadline = Date.now() + waitMs;

    let slot = await this.primeEntry(id, signal);
    while (slot) {
      if (signal.aborted) return null;
      if (slot.entry.id !== id) return null;
      if (slot.audio.ready) {
        const prepared = await this.decode(slot, signal);
        if (prepared) return { slot, entry: slot.entry, prepared };
      }
      if (Date.now() + pollMs > deadline) return null;
      await sleep(pollMs, signal);
      if (signal.aborted) return null;
      slot = await this.primeEntry(id, signal);
    }
    return null;
  }

  private primeEntry(id: string, signal: AbortSignal): Promise<BroadcastSlot | null> {
    return requestEntry(this.options.origin, id, {
      fetchImpl: this.options.fetchImpl,
      signal,
    });
  }

  /**
   * The entry and its audio for a slot, polling while the Worker produces it.
   *
   * Bounded twice: by `waitMs`, and by the slot's own end, because a clip that
   * arrives after the slot has passed is never played.
   */
  async resolve(slotIndex: number, signal: AbortSignal): Promise<ResolvedSlot | null> {
    const waitMs = this.options.waitMs ?? BROADCAST_WAIT_MS;
    const pollMs = this.options.pollMs ?? BROADCAST_POLL_MS;
    const deadline = Date.now() + waitMs;

    let slot = await this.prime(slotIndex, signal);
    while (slot) {
      if (signal.aborted) return null;
      // The Worker clamps out-of-range requests, so a different slot means this
      // ask was stale. Preparing it would attach the wrong audio to the slot.
      if (slot.slotIndex !== slotIndex) return null;

      if (slot.audio.ready) {
        const prepared = await this.decode(slot, signal);
        if (prepared) return { slot, entry: slot.entry, prepared };
      }

      const remainingMs = slot.slotEndMs - slot.serverMs;
      if (remainingMs < pollMs) return null;
      if (Date.now() + pollMs > deadline) return null;

      await sleep(pollMs, signal);
      if (signal.aborted) return null;
      slot = await this.prime(slotIndex, signal);
    }
    return null;
  }

  /** Download and decode the clip, or null if the bytes are unusable. */
  private async decode(
    slot: BroadcastSlot,
    signal: AbortSignal,
  ): Promise<PreparedNarration | null> {
    const bytes = await fetchCachedAudio(this.options.origin, slot.audio.key, {
      fetchImpl: this.options.fetchImpl,
      signal,
      budgetMs: AUDIO_FETCH_BUDGET_MS,
    });
    if (!bytes || signal.aborted) return null;
    try {
      // `decodeAudioData` detaches its input, so hand it a copy.
      const buffer = await this.options.context.decodeAudioData(bytes.slice(0));
      if (signal.aborted) return null;
      return {
        slotIndex: slot.slotIndex,
        entryId: slot.entry.id,
        text: slot.entry.description,
        buffer,
        durationSec: buffer.duration,
      };
    } catch {
      return null;
    }
  }

  async prepare(request: PrepareRequest, signal: AbortSignal): Promise<PreparedNarration> {
    const resolved = await this.resolve(request.slotIndex, signal);
    if (!resolved) throw new BroadcastMissError(request.slotIndex, "no clip");
    return resolved.prepared;
  }

  play(prepared: PreparedNarration, options: PlayOptions): PlaybackHandle {
    if (!prepared.buffer) throw new Error("broadcast narration requires a buffer");
    return playPreparedBuffer(this.options.context, this.options.voiceBus, prepared, options);
  }

  dispose(): void {
    // No owned resources.
  }
}
